import { NavigationView, NAVIGATION_VIEW_LABELS, NAVIGATION_VIEW_SYS_ADMIN_LEGADO, normalizeNavigationView } from '@/shared/constants/navigation-view.enum';
import { isSysAdminUserId } from '@/shared/navigation/navigation-view.utils';

/** Visões definidas manualmente no cadastro (demais valores no banco = automática). */
export const VISAO_EXPLICITA_NUMEROS = [
  NavigationView.Administrador,
  NavigationView.Corregedoria,
  NavigationView.Subcorregedoria,
  NavigationView.RecursosHumanos,
  NavigationView.Auxiliar,
] as const;

export type VisaoSistemaDoc = {
  /**
   * Texto curto (1–2 frases) para explicar o propósito da visão.
   */
  descricao: string;
  /**
   * Itens opcionais listando o que a visão faz no dia a dia.
   */
  bullets?: string[];
};

/**
 * Documentação das visões fixas do SGI (aba Visões em Configurações).
 *
 * Observação:
 * - "Defensor(a) Diretor(a)" funciona como "Defensor(a)" para fins operacionais.
 * - "Candidato / Processo seletivo" não faz parte deste enum: existe um fluxo público separado.
 */
export const VISAO_SGI_DOCS: Readonly<Record<NavigationView, VisaoSistemaDoc>> = {
  [NavigationView.Administrador]: {
    descricao:
      'Acesso administrativo completo ao SGI (gestão, suporte, auditorias e configurações).',
    bullets: ['Acesso completo a todos os módulos do SGI.'],
  },
  [NavigationView.Corregedoria]: {
    descricao:
      'Cuida de ações relativas a defensores: acompanhamento, validações e rotinas de gestão.',
    bullets: [
      'Gerencia cadastros e ações vinculadas a defensores.',
      'Apoia regras e validações relacionadas ao trabalho dos defensores.',
      'Acompanha relatórios e movimentações pertinentes.',
    ],
  },
  [NavigationView.Subcorregedoria]: {
    descricao:
      'Cuida de ações relativas a estagiários: supervisão, acompanhamento e rotinas de frequência.',
    bullets: [
      'Gerencia rotinas e cadastros de estagiários e seus contextos.',
      'Acompanha frequência e pendências.',
      'Utiliza relatórios para supervisionar o processo dos estagiários.',
    ],
  },
  [NavigationView.DefensorDiretor]: {
    descricao:
      'Equivale à visão Defensor(a) para fins operacionais (diretor regional não altera as permissões básicas).',
    bullets: [
      'Preenche relatórios e acompanha rotinas do defensor.',
      'Valida folhas de frequência no contexto do seu trabalho.',
    ],
  },
  [NavigationView.Defensor]: {
    descricao:
      'Acesso via token para atuação profissional: preenchimento de relatórios e validação de frequência.',
    bullets: [
      'Preenche relatórios vinculados às rotinas do seu perfil.',
      'Valida folhas de frequência.',
      'Acessa apenas o que é necessário para a atuação do defensor.',
    ],
  },
  [NavigationView.Estagiario]: {
    descricao:
      'Acesso via token para preenchimento de frequência (estagiário(a) preenche suas folhas).',
    bullets: [
      'Preenche as folhas de frequência.',
      'Acompanha dados essenciais da sua atuação.',
    ],
  },
  [NavigationView.Colaborador]: {
    descricao:
      'Visão para demais colaboradores: costuma focar em cadastros e acompanhamento de informações.',
    bullets: [
      'Acesso a cadastros, lotações e rotinas administrativas permitidas.',
      'Pode acompanhar telas relacionadas à supervisão, conforme permissões efetivas.',
    ],
  },
  [NavigationView.RecursosHumanos]: {
    descricao:
      'Gestão de cadastros de pessoas: defensores, estagiários e colaboradores.',
    bullets: [
      'Cadastra, edita e inativa perfis de defensores, estagiários e colaboradores.',
      'Mantém dados cadastrais das pessoas vinculadas a esses perfis.',
      'Consulta tabelas de vínculos e lotações (somente leitura).',
    ],
  },
  [NavigationView.Auxiliar]: {
    descricao:
      'Consulta cadastros de pessoas e da estrutura organizacional, sem permissão de alteração.',
    bullets: [
      'Visualiza defensores, estagiários, colaboradores e pessoas.',
      'Consulta diretorias, defensorias, núcleos, comarcas e demais cadastros organizacionais e locais.',
      'Não pode criar, editar nem excluir registros.',
    ],
  },
};

export function isVisaoAdministrador(visao: number | string | null | undefined): boolean {
  return normalizeNavigationView(visao) === NavigationView.Administrador;
}

export function isUsuarioAdministrador(
  usuario: { id?: number; visao?: number | string | null } | null | undefined,
): boolean {
  if (!usuario) return false;
  if (isSysAdminUserId(usuario.id)) return true;
  return isVisaoAdministrador(usuario.visao);
}

export function isVisaoExplicitaUsuario(visao: number | null | undefined): boolean {
  if (visao == null || !Number.isFinite(Number(visao))) return false;
  const v = Number(visao);
  if (v === NAVIGATION_VIEW_SYS_ADMIN_LEGADO) return true;
  return (VISAO_EXPLICITA_NUMEROS as readonly number[]).includes(v);
}

/** Valor do `<select>`: só 1/2/3 ou vazio (automática). */
export function toUsuarioVisaoFormValue(visao: number | string | null | undefined): string {
  if (visao == null || visao === '') return '';
  const num = typeof visao === 'number' ? visao : Number(visao);
  if (!Number.isFinite(num)) return '';
  return isVisaoExplicitaUsuario(num) ? String(num) : '';
}

/** Visões atribuídas manualmente no cadastro do usuário. */
export const VISAO_EXPLICITA_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Automática (conforme perfil da pessoa)' },
  {
    value: String(NavigationView.Administrador),
    label: NAVIGATION_VIEW_LABELS[NavigationView.Administrador],
  },
  {
    value: String(NavigationView.Corregedoria),
    label: NAVIGATION_VIEW_LABELS[NavigationView.Corregedoria],
  },
  {
    value: String(NavigationView.Subcorregedoria),
    label: NAVIGATION_VIEW_LABELS[NavigationView.Subcorregedoria],
  },
  {
    value: String(NavigationView.RecursosHumanos),
    label: NAVIGATION_VIEW_LABELS[NavigationView.RecursosHumanos],
  },
  {
    value: String(NavigationView.Auxiliar),
    label: NAVIGATION_VIEW_LABELS[NavigationView.Auxiliar],
  },
];

/** Opções de visão disponíveis no cadastro conforme permissão do usuário logado. */
export function visaoExplicitaOptionsParaCadastro(ehAdministrador: boolean) {
  return VISAO_EXPLICITA_OPTIONS.filter(
    (item) => ehAdministrador || item.value !== String(NavigationView.Administrador),
  );
}

export function labelVisaoUsuario(visao: number | null | undefined): string {
  if (!isVisaoExplicitaUsuario(visao)) {
    return 'Automática';
  }
  const key = normalizeNavigationView(visao);
  return key != null ? NAVIGATION_VIEW_LABELS[key] : `Visão ${visao}`;
}

/** Visões com acesso por token ou escopo operacional limitado — sem tela "Meu Perfil". */
const VISOES_SEM_MEU_PERFIL = new Set<NavigationView>([
  NavigationView.Defensor,
  NavigationView.DefensorDiretor,
  NavigationView.Estagiario,
]);

export function visaoExibeMeuPerfil(visao: NavigationView | null): boolean {
  if (visao == null) return false;
  return !VISOES_SEM_MEU_PERFIL.has(visao);
}
